import { useState, useEffect } from "react";
import "../users.css";

/* ── Inline SVG Icons (Lucide-style) ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75, className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    roles: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    departments: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    search: ['M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z'],
    export: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    plus: ['M12 5v14', 'M5 12h14'],
    trash: ['M3 6h18', 'M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6', 'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2', 'M10 11v6', 'M14 11v6'],
};

export default function Users({ user }) {
    const canManage = (perm) => {
        if (user?.userType === 'admin' && !user?.operatorId) return true; // System SuperAdmin
        if (user?.userType === 'operator' && user?._id === user?.operatorId) return true; // Main Operator account
        return user?.permissions?.includes(perm);
    };

    const [activeTab, setActiveTab] = useState("users");
    const [showCreateRole, setShowCreateRole] = useState(false);
    const [showCreateDept, setShowCreateDept] = useState(false);
    const [showCreateUser, setShowCreateUser] = useState(false);

    // Editing states
    const [editingUser, setEditingUser] = useState(null);
    const [editingRole, setEditingRole] = useState(null);
    const [editingDept, setEditingDept] = useState(null);

    // Data states
    const [usersList, setUsersList] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [deptsList, setDeptsList] = useState([]);

    // Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("All Roles");
    const [statusFilter, setStatusFilter] = useState("All Status");

    const [operatorsList, setOperatorsList] = useState([]);
    const [operatorFilter, setOperatorFilter] = useState(user?.userType === 'operator' ? user.operatorId : "system");

    const fetchData = async () => {
        try {
            const opId = user?.userType === 'operator' ? user.operatorId : operatorFilter;
            const [uRes, rRes, dRes] = await Promise.all([
                fetch(`/api/users/${opId}`),
                fetch(`/api/roles/${opId}`),
                fetch(`/api/departments/${opId}`)
            ]);

            const uData = uRes.ok ? await uRes.json() : [];
            const rData = rRes.ok ? await rRes.json() : [];
            const dData = dRes.ok ? await dRes.json() : [];

            setUsersList(Array.isArray(uData) ? uData : []);
            setRolesList(Array.isArray(rData) ? rData : []);
            setDeptsList(Array.isArray(dData) ? dData : []);

            if (user?.userType !== 'operator') {
                const oRes = await fetch("/api/operators");
                setOperatorsList(await oRes.json());
            }
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    useEffect(() => {
        fetchData();
    }, [operatorFilter]);

    const tabs = [
        { id: "users", label: "Users", icon: icons.users },
        { id: "roles", label: "Roles", icon: icons.roles },
        { id: "departments", label: "Departments", icon: icons.departments },
    ];

    const handleEdit = (type, item) => {
        if (type === 'user') {
            setEditingUser(item);
            setShowCreateUser(true);
        } else if (type === 'role') {
            setEditingRole(item);
            setShowCreateRole(true);
        } else if (type === 'department') {
            setEditingDept(item);
            setShowCreateDept(true);
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
        try {
            const res = await fetch(`/api/${type}s/${id}`, {
                method: 'DELETE',
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            if (res.ok) fetchData();
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const CreateUserModal = ({ editingItem }) => {
        const [step, setStep] = useState(1);
        const [formData, setFormData] = useState(editingItem || {
            firstName: '', lastName: '', email: '', phone: '', password: '',
            role: '', department: '', permissions: [], isActive: true,
            operatorId: user?.userType === 'operator' ? user.operatorId : ''
        });

        const permissionsList = [
            "Manage Users", "Manage Roles", "Manage Departments",
            "Create Operator", "Edit Operator", "Delete Operator",
            "Create Price Plan", "Edit Price Plan", "Delete Price Plan",
            "Create Subscription", "Edit Subscription", "Delete Subscription",
            "Manage Fleet", "Manage Trips", "Manage Drivers", "Manage Vendors", "Manage Clients", "View Reports"
        ];

        const handleSave = async () => {
            try {
                const url = editingItem ? `/api/users/${editingItem._id}` : "/api/users";
                const method = editingItem ? 'PUT' : 'POST';

                const res = await fetch(url, {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                        'adminName': `${user?.firstName} ${user?.lastName}`,
                        'adminEmail': user?.email
                    },
                    body: JSON.stringify(formData)
                });
                if (res.ok) {
                    setShowCreateUser(false);
                    setEditingUser(null);
                    fetchData();
                }
            } catch (err) {
                console.error("Save user error:", err);
            }
        };

        const togglePermission = (perm) => {
            setFormData(prev => ({
                ...prev,
                permissions: prev.permissions.includes(perm)
                    ? prev.permissions.filter(p => p !== perm)
                    : [...prev.permissions, perm]
            }));
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content" style={{ maxWidth: '600px' }}>
                    <div className="modal-header">
                        <h2>{editingItem ? 'Edit User' : 'Create New User'}</h2>
                        <button className="close-btn" onClick={() => { setShowCreateUser(false); setEditingUser(null); }}>
                            <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={20} />
                        </button>
                    </div>

                    <div className="modal-steps">
                        <button className={`step-item ${step === 1 ? 'active' : ''}`} onClick={() => setStep(1)}>Basic Info</button>
                        <button className={`step-item ${step === 2 ? 'active' : ''}`} onClick={() => setStep(2)}>Permissions</button>
                        <button className={`step-item ${step === 3 ? 'active' : ''}`} onClick={() => setStep(3)}>Department</button>
                    </div>

                    <div className="modal-body">
                        {step === 1 && (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">First Name *</label>
                                        <input type="text" className="form-input" placeholder="Enter first name" value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last Name *</label>
                                        <input type="text" className="form-input" placeholder="Enter last name" value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email Address *</label>
                                    <input type="email" className="form-input" placeholder="Enter email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Phone Number</label>
                                    <input type="text" className="form-input" placeholder="Enter phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Temporary Password *</label>
                                    <input type="password" className="form-input" placeholder="Enter password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
                                </div>
                                <div className="toggle-group">
                                    <label className="toggle-switch">
                                        <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">Account Active</span>
                                    <span className="active-pill">{formData.isActive ? 'Active' : 'Inactive'}</span>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <div className="form-group">
                                    <label className="form-label">User Role *</label>
                                    <select className="form-select" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                                        <option value="">Select Role</option>
                                        {Array.isArray(rolesList) && rolesList.map(r => <option key={r._id} value={r.name}>{r.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Additional Permissions</label>
                                    <div className="permissions-container">
                                        <div className="permissions-grid">
                                            {permissionsList.map((perm, i) => (
                                                <label key={i} className="permission-item">
                                                    <input type="checkbox" checked={formData.permissions.includes(perm)} onChange={() => togglePermission(perm)} />
                                                    {perm}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <div className="form-group">
                                    <label className="form-label">Department</label>
                                    <select className="form-select" value={formData.department} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                        <option value="">Assign Department</option>
                                        {Array.isArray(deptsList) && deptsList.map(d => <option key={d._id} value={d.name}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Start Date</label>
                                    <input type="date" className="form-input" value={formData.startDate?.split('T')[0] || ''} onChange={e => setFormData({ ...formData, startDate: e.target.value })} />
                                </div>
                                {user?.userType !== 'operator' && (
                                    <div className="form-group">
                                        <label className="form-label">Assign Operator</label>
                                        <select className="form-select" value={formData.operatorId} onChange={e => setFormData({ ...formData, operatorId: e.target.value })}>
                                            <option value="">System Admin (No Operator)</option>
                                            {Array.isArray(operatorsList) && operatorsList.map(op => <option key={op._id} value={op._id}>{op.companyName}</option>)}
                                        </select>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn-cancel" onClick={() => { setShowCreateUser(false); setEditingUser(null); }}>Cancel</button>
                        <button className="btn-save" onClick={handleSave}>{editingItem ? 'Update User' : 'Invite User'}</button>
                    </div>
                </div>
            </div>
        );
    };

    const CreateDepartmentModal = ({ editingItem }) => {
        const [formData, setFormData] = useState(editingItem || {
            name: '',
            description: '',
            manager: '',
            isActive: true,
            operatorId: user?.userType === 'operator' ? user.operatorId : ''
        });

        const handleSave = async () => {
            try {
                const url = editingItem ? `/api/departments/${editingItem._id}` : "/api/departments";
                const method = editingItem ? 'PUT' : 'POST';

                const res = await fetch(url, {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                        'adminName': `${user?.firstName} ${user?.lastName}`,
                        'adminEmail': user?.email
                    },
                    body: JSON.stringify(formData)
                });
                if (res.ok) {
                    setShowCreateDept(false);
                    setEditingDept(null);
                    fetchData();
                }
            } catch (err) {
                console.error("Save dept error:", err);
            }
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2>{editingItem ? 'Edit Department' : 'New Department'}</h2>
                        <button className="close-btn" onClick={() => { setShowCreateDept(false); setEditingDept(null); }}>
                            <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <label className="form-label">Department Name *</label>
                            <input type="text" className="form-input" placeholder="Enter department name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea className="form-textarea" placeholder="Enter description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}></textarea>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Manager</label>
                            <input type="text" className="form-input" placeholder="Type manager name" value={formData.manager} onChange={e => setFormData({ ...formData, manager: e.target.value })} />
                        </div>
                        <div className="toggle-group">
                            <label className="toggle-switch">
                                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="toggle-label">Active Status</span>
                        </div>
                        {user?.userType !== 'operator' && (
                            <div className="form-group" style={{ marginTop: '20px' }}>
                                <label className="form-label">Assign Operator</label>
                                <select className="form-select" value={formData.operatorId} onChange={e => setFormData({ ...formData, operatorId: e.target.value })}>
                                    <option value="">System Admin (No Operator)</option>
                                    {Array.isArray(operatorsList) && operatorsList.map(op => <option key={op._id} value={op._id}>{op.companyName}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn-cancel" onClick={() => { setShowCreateDept(false); setEditingDept(null); }}>Cancel</button>
                        <button className="btn-save" onClick={handleSave}>{editingItem ? 'Update Department' : 'Save Department'}</button>
                    </div>
                </div>
            </div>
        );
    };

    const CreateRoleModal = ({ editingItem }) => {
        const [formData, setFormData] = useState(editingItem || {
            name: '',
            description: '',
            permissions: [],
            isActive: true,
            operatorId: user?.userType === 'operator' ? user.operatorId : ''
        });
        const permissionsList = [
            "Manage Users", "Manage Roles", "Manage Departments",
            "Create Operator", "Edit Operator", "Delete Operator",
            "Create Price Plan", "Edit Price Plan", "Delete Price Plan",
            "Create Subscription", "Edit Subscription", "Delete Subscription",
            "Manage Fleet", "Manage Trips", "Manage Drivers", "Manage Vendors", "Manage Clients", "View Reports"
        ];

        const handleSave = async () => {
            try {
                const url = editingItem ? `/api/roles/${editingItem._id}` : "/api/roles";
                const method = editingItem ? 'PUT' : 'POST';

                const res = await fetch(url, {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                        'adminName': `${user?.firstName} ${user?.lastName}`,
                        'adminEmail': user?.email
                    },
                    body: JSON.stringify(formData)
                });
                if (res.ok) {
                    setShowCreateRole(false);
                    setEditingRole(null);
                    fetchData();
                }
            } catch (err) {
                console.error("Save role error:", err);
            }
        };

        const togglePermission = (perm) => {
            setFormData(prev => ({
                ...prev,
                permissions: prev.permissions.includes(perm)
                    ? prev.permissions.filter(p => p !== perm)
                    : [...prev.permissions, perm]
            }));
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2>{editingItem ? 'Edit Role' : 'New Role'}</h2>
                        <button className="close-btn" onClick={() => { setShowCreateRole(false); setEditingRole(null); }}>
                            <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <label className="form-label">Role Name *</label>
                            <input type="text" className="form-input" placeholder="Enter role name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea className="form-textarea" placeholder="Enter description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}></textarea>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Permissions</label>
                            <div className="permissions-container">
                                <div className="permissions-grid">
                                    {permissionsList.map((perm, i) => (
                                        <label key={i} className="permission-item">
                                            <input type="checkbox" checked={formData.permissions.includes(perm)} onChange={() => togglePermission(perm)} />
                                            {perm}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="toggle-group">
                            <label className="toggle-switch">
                                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="toggle-label">Active Status</span>
                        </div>
                        {user?.userType !== 'operator' && (
                            <div className="form-group" style={{ marginTop: '20px' }}>
                                <label className="form-label">Assign Operator</label>
                                <select className="form-select" value={formData.operatorId} onChange={e => setFormData({ ...formData, operatorId: e.target.value })}>
                                    <option value="">System Admin (No Operator)</option>
                                    {Array.isArray(operatorsList) && operatorsList.map(op => <option key={op._id} value={op._id}>{op.companyName}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn-cancel" onClick={() => { setShowCreateRole(false); setEditingRole(null); }}>Cancel</button>
                        <button className="btn-save" onClick={handleSave}>{editingItem ? 'Update Role' : 'Save Role'}</button>
                    </div>
                </div>
            </div>
        );
    };

    const renderFilters = () => {
        if (activeTab === "users") {
            return (
                <div className="filters-bar">
                    <div className="search-input-wrap">
                        <Icon d={icons.search} size={16} className="search-icon-inline" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-selects">
                        <select
                            className="filter-select"
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                        >
                            <option>All Roles</option>
                            {Array.isArray(rolesList) && rolesList.map(role => (
                                <option key={role._id} value={role.name}>{role.name}</option>
                            ))}
                        </select>
                        {user?.userType !== 'operator' && (
                            <select
                                className="filter-select"
                                value={operatorFilter}
                                onChange={(e) => setOperatorFilter(e.target.value)}
                            >
                                <option value="all">All Operators</option>
                                <option value="system">System Admins</option>
                                {Array.isArray(operatorsList) && operatorsList.map(op => (
                                    <option key={op._id} value={op._id}>{op.companyName}</option>
                                ))}
                            </select>
                        )}
                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option>All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                    <div className="action-btns">
                        {canManage('Manage Users') && (
                            <button className="btn-primary" onClick={() => setShowCreateUser(true)}>
                                <Icon d={icons.plus} size={16} /> Create/Invite User
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        if (activeTab === "roles") {
            return (
                <div className="filters-bar" style={{ justifyContent: 'flex-end' }}>
                    {canManage('Manage Roles') && (
                        <button className="btn-primary" onClick={() => setShowCreateRole(true)}>
                            <Icon d={icons.plus} size={16} /> Add New Role
                        </button>
                    )}
                </div>
            );
        }

        if (activeTab === "departments") {
            return (
                <div className="filters-bar" style={{ justifyContent: 'flex-end' }}>
                    {canManage('Manage Departments') && (
                        <button className="btn-primary" onClick={() => setShowCreateDept(true)}>
                            <Icon d={icons.plus} size={16} /> Add Department
                        </button>
                    )}
                </div>
            );
        }
    };

    const renderTable = () => {
        const filteredUsers = (Array.isArray(usersList) ? usersList : []).filter(u => {
            const matchesSearch = (u.firstName + " " + u.lastName + " " + u.email)
                .toLowerCase()
                .includes(searchTerm.toLowerCase());

            const matchesRole = roleFilter === "All Roles" || u.role === roleFilter;

            const statusStr = u.isActive ? "Active" : "Inactive";
            const matchesStatus = statusFilter === "All Status" || statusStr === statusFilter;

            return matchesSearch && matchesRole && matchesStatus;
        });

        switch (activeTab) {
            case "users":
                return (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>User Name</th>
                                <th>Email</th>
                                {user?.userType !== 'operator' && <th>Operator</th>}
                                <th>Role</th>
                                <th>Department</th>
                                <th>Status</th>
                                <th>Created At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? filteredUsers.map(u => (
                                <tr key={u._id}>
                                    <td>{u.firstName} {u.lastName}</td>
                                    <td>{u.email}</td>
                                    {user?.userType !== 'operator' && (
                                        <td style={{ fontWeight: 600, color: '#4f46e5' }}>
                                            {operatorsList.find(op => op._id === u.operatorId)?.companyName || 'System Admin'}
                                        </td>
                                    )}
                                    <td>{u.role}</td>
                                    <td>{u.department}</td>
                                    <td><span className={`status-badge ${u.isActive ? 'active' : 'inactive'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        {canManage('Manage Users') && (
                                            <>
                                                <button className="icon-btn edit" onClick={() => handleEdit('user', u)}>
                                                    <Icon d={['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z']} size={16} />
                                                </button>
                                                <button className="icon-btn delete" onClick={() => handleDelete('user', u._id)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="empty-state">No users found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                );
            case "roles":
                return (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>Role Name</th>
                                <th>Description</th>
                                <th>Permissions</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(rolesList) && rolesList.length > 0 ? rolesList.map(r => (
                                <tr key={r._id}>
                                    <td>{r.name}</td>
                                    <td>{r.description}</td>
                                    <td>{r.permissions?.length || 0} Permissions</td>
                                    <td><span className={`status-badge ${r.isActive ? 'active' : 'inactive'}`}>{r.isActive ? 'Active' : 'Inactive'}</span></td>
                                    <td>
                                        {canManage('Manage Roles') && (
                                            <>
                                                <button className="icon-btn edit" onClick={() => handleEdit('role', r)}>
                                                    <Icon d={['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z']} size={16} />
                                                </button>
                                                <button className="icon-btn delete" onClick={() => handleDelete('role', r._id)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="empty-state">No roles found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                );
            case "departments":
                return (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>Department Name</th>
                                <th>Description</th>
                                <th>Manager</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(deptsList) && deptsList.length > 0 ? deptsList.map(d => (
                                <tr key={d._id}>
                                    <td>{d.name}</td>
                                    <td>{d.description}</td>
                                    <td>{d.manager}</td>
                                    <td><span className={`status-badge ${d.isActive ? 'active' : 'inactive'}`}>{d.isActive ? 'Active' : 'Inactive'}</span></td>
                                    <td>
                                        {canManage('Manage Departments') && (
                                            <>
                                                <button className="icon-btn edit" onClick={() => handleEdit('department', d)}>
                                                    <Icon d={['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z']} size={16} />
                                                </button>
                                                <button className="icon-btn delete" onClick={() => handleDelete('department', d._id)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="empty-state">No departments found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                );
            default:
                return null;
        }
    };

    return (
        <div className="users-page">
            <div className="users-header">
                <h1>User Management</h1>
                <p>Manage system access, roles, and permissions.</p>
            </div>

            <div className="users-tabs">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <Icon d={tab.icon} size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {renderFilters()}

            <div className="premium-table-container">
                {renderTable()}
            </div>

            {showCreateUser && <CreateUserModal editingItem={editingUser} />}
            {showCreateDept && <CreateDepartmentModal editingItem={editingDept} />}
            {showCreateRole && <CreateRoleModal editingItem={editingRole} />}
        </div>
    );
}